import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getPublicDonors } from '@/services/donors.api';
import { FrontendDonor } from '@/services/transform';
import PartnerDetailModal from './components/PartnerDetailModal';
import PartnershipApplyForm from './components/PartnershipApplyForm';

// 本地类型定义（从 API 数据派生）
type PartnerType = 'university' | 'research' | 'enterprise' | 'nonprofit' | 'partner' | 'organization' | 'individual' | 'donor';
type PartnerRegion = 'asia' | 'europe' | 'americas' | 'africa' | 'oceania' | '';
type DonorTier = 'platinum' | 'gold' | 'silver' | 'bronze' | 'diamond' | 'honorary' | 'strategic';

interface Partner extends FrontendDonor {
  type: PartnerType;
  region: PartnerRegion;
}

const HERO_BG = '/images/hero-bg-partners.jpg';

type ViewMode = 'grid' | 'list';

/* ──────────────── Partner Card ──────────────── */
function PartnerCard({ partner, isZh, view, onSelect }: {
  partner: Partner; isZh: boolean; view: ViewMode; onSelect: (p: Partner) => void;
}) {
  const { t } = useTranslation();

  if (view === 'list') {
    return (
      <div
        className="bg-white border border-[#0A2540]/10 rounded-xl p-5 flex items-center gap-5 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
        onClick={() => onSelect(partner)}
      >
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0"
          style={{ backgroundColor: partner.color }}
        >
          {partner.abbr.length > 4 ? partner.abbr.slice(0, 4) : partner.abbr}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-1">
            <h3 className="font-bold text-[#0A2540] text-base truncate">
              {isZh ? partner.name : partner.nameEn}
            </h3>
            <span className="text-[#0A2540]/40 text-xs shrink-0">{partner.year}</span>
          </div>
          <p className="text-[#0A2540]/60 text-sm line-clamp-1 mb-1">
            {isZh ? partner.description : partner.descriptionEn}
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-1 text-[#2C7DA0] text-xs font-medium whitespace-nowrap cursor-pointer shrink-0">
          {t('partnersPage.visitWebsite')}
          <i className="ri-arrow-right-line" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="group bg-white border border-[#0A2540]/10 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-200 cursor-pointer"
      onClick={() => onSelect(partner)}
    >
      <div className="h-2" style={{ backgroundColor: partner.color }} />
      <div className="p-6">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-sm mb-4"
          style={{ backgroundColor: partner.color }}
        >
          {partner.abbr.length > 4 ? partner.abbr.slice(0, 4) : partner.abbr}
        </div>
        <h3 className="font-bold text-[#0A2540] text-base mb-0.5">
          {isZh ? partner.name : partner.nameEn}
        </h3>
        <p className="text-[#0A2540]/40 text-xs mb-3">{partner.year}</p>
        <p className="text-[#0A2540]/60 text-sm leading-relaxed line-clamp-3 mb-4">
          {isZh ? partner.description : partner.descriptionEn}
        </p>
        <div className="pt-4 border-t border-[#0A2540]/5">
          <div className="inline-flex items-center gap-1 text-[#2C7DA0] text-xs font-medium cursor-pointer group-hover:underline whitespace-nowrap">
            {t('common.readMore')}
            <i className="ri-arrow-right-line" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Donor Tier Config ──────────────── */
const TIER_META: Record<DonorTier, { zhLabel: string; enLabel: string; icon: string; cls: string; badgeCls: string }> = {
  platinum:  { zhLabel: '铂金捐赠人', enLabel: 'Platinum Donor',    icon: 'ri-vip-diamond-line', cls: 'border-[#4682B4]/30 bg-[#4682B4]/5', badgeCls: 'bg-[#4682B4] text-white' },
  diamond:  { zhLabel: '钻石捐赠人', enLabel: 'Diamond Donor',    icon: 'ri-vip-diamond-line', cls: 'border-[#4682B4]/30 bg-[#4682B4]/5', badgeCls: 'bg-[#4682B4] text-white' },
  gold:     { zhLabel: '金牌捐赠人', enLabel: 'Gold Donor',        icon: 'ri-medal-line',        cls: 'border-[#C9A84C]/30 bg-[#C9A84C]/5', badgeCls: 'bg-[#C9A84C] text-white' },
  silver:   { zhLabel: '银牌捐赠人', enLabel: 'Silver Donor',      icon: 'ri-award-line',        cls: 'border-[#6C757D]/30 bg-[#6C757D]/5', badgeCls: 'bg-[#6C757D] text-white' },
  bronze:   { zhLabel: '铜牌捐赠人', enLabel: 'Bronze Donor',      icon: 'ri-award-line',        cls: 'border-[#CD7F32]/30 bg-[#CD7F32]/5', badgeCls: 'bg-[#CD7F32] text-white' },
  honorary: { zhLabel: '荣誉捐赠人', enLabel: 'Honorary Donor',    icon: 'ri-star-line',         cls: 'border-[#6A5ACD]/30 bg-[#6A5ACD]/5', badgeCls: 'bg-[#6A5ACD] text-white' },
  strategic:{ zhLabel: '战略伙伴',   enLabel: 'Strategic Partner', icon: 'ri-team-line',         cls: 'border-[#2C7DA0]/30 bg-[#2C7DA0]/5', badgeCls: 'bg-[#2C7DA0] text-white' },
};

// 默认 tier 配置（用于安全访问）
const DEFAULT_TIER_META = { zhLabel: '合作伙伴', enLabel: 'Partner', icon: 'ri-user-line', cls: 'border-[#0A2540]/10 bg-[#0A2540]/5', badgeCls: 'bg-[#0A2540] text-white' };

const TIER_FILTER_ORDER: Array<DonorTier | 'all'> = ['all', 'platinum', 'diamond', 'gold', 'silver', 'bronze', 'honorary', 'strategic'];

/* ──────────────── Regular Donor Card ──────────────── */
function DonorCard({ donor, isZh, index }: { donor: FrontendDonor; isZh: boolean; index: number }) {
  const [vis, setVis] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const meta = TIER_META[donor.tier] || DEFAULT_TIER_META;

  useEffect(() => {
    const timer = setTimeout(() => {
      const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.1 });
      if (ref.current) obs.observe(ref.current);
      return () => obs.disconnect();
    }, index * 60);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <div
      ref={ref}
      className={`bg-white rounded-2xl border ${meta.cls} overflow-hidden transition-all duration-500 hover:-translate-y-1 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
      style={{ transitionDelay: `${index * 50}ms` }}
    >
      <div className="h-1" style={{ backgroundColor: donor.color }} />
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xs shrink-0"
            style={{ backgroundColor: donor.color }}
          >
            {donor.abbr.length > 4 ? donor.abbr.slice(0, 4) : donor.abbr}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-[#0A2540] text-sm leading-snug mb-1 truncate">
              {isZh ? donor.name : donor.nameEn}
            </h3>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.badgeCls}`}>
              <i className={`${meta.icon} text-[10px]`} />
              {isZh ? meta.zhLabel : meta.enLabel}
            </span>
          </div>
        </div>
        <p className="text-[#0A2540]/55 text-xs leading-relaxed mb-3 line-clamp-2">
          {isZh ? donor.description : donor.descriptionEn}
        </p>
        <div className="flex items-center gap-1 text-[#0A2540]/35 text-[10px]">
          <i className="ri-calendar-line" />
          <span>{isZh ? `${donor.year}年起持续支持` : `Supporting since ${donor.year}`}</span>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Diamond Big Card ──────────────── */
function DiamondBigCard({ donor, isZh, index }: { donor: FrontendDonor; isZh: boolean; index: number }) {
  const [vis, setVis] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.1 });
      if (ref.current) obs.observe(ref.current);
      return () => obs.disconnect();
    }, index * 120);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <div
      ref={ref}
      className={`relative rounded-2xl overflow-hidden transition-all duration-700 hover:-translate-y-1.5 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{
        background: `linear-gradient(135deg, #0A2540 0%, #1B4F72 60%, ${donor.color}55 100%)`,
        transitionDelay: `${index * 100}ms`,
      }}
    >
      {/* Decorative glow */}
      <div
        className="absolute top-0 right-0 w-48 h-48 rounded-full"
        style={{ background: `radial-gradient(circle, ${donor.color}30, transparent)`, transform: 'translate(30%, -30%)' }}
      />
      <div
        className="absolute bottom-0 left-0 w-32 h-32 rounded-full"
        style={{ background: `radial-gradient(circle, ${donor.color}20, transparent)`, transform: 'translate(-40%, 40%)' }}
      />
      {/* Gold top accent line */}
      <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, transparent, #E8A838, #F5C842, #E8A838, transparent)' }} />

      <div className="relative z-10 p-6">
        {/* Header row */}
        <div className="flex items-start gap-4 mb-5">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-black text-sm shrink-0 ring-2 ring-white/20"
            style={{ backgroundColor: donor.color, boxShadow: `0 0 20px ${donor.color}50` }}
          >
            {donor.abbr.length > 4 ? donor.abbr.slice(0, 4) : donor.abbr}
          </div>
          <div className="flex-1 min-w-0 pt-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E8A838]/20 border border-[#E8A838]/30">
                <i className="ri-vip-diamond-fill text-[#E8A838] text-[10px]" />
                <span className="text-[#E8A838] text-[10px] font-bold tracking-wider">
                  {isZh ? '钻石捐赠人' : 'Diamond Donor'}
                </span>
              </div>
            </div>
            <h3 className="text-white font-bold text-lg leading-snug">
              {isZh ? donor.name : donor.nameEn}
            </h3>
          </div>
        </div>

        <div className="h-px bg-white/10 mb-4" />

        {/* Quote description */}
        <div className="flex items-start gap-2.5 mb-5">
          <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
            <i className="ri-double-quotes-l text-[#E8A838]/60 text-sm" />
          </div>
          <p className="text-white/75 text-sm leading-relaxed">
            {isZh ? donor.description : donor.descriptionEn}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-[#E8A838] text-[10px]" />
              ))}
            </div>
            <span className="text-white/40 text-xs">
              {isZh ? `${donor.year} 年起` : `Since ${donor.year}`}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 border border-white/15">
            <i className="ri-heart-fill text-[#E8A838] text-xs" />
            <span className="text-white/80 text-xs font-medium whitespace-nowrap">
              {isZh ? '荣誉支持者' : 'Honored Patron'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Diamond Featured Section ──────────────── */
function DiamondFeaturedSection({ donorsData, isZh, visible }: { donorsData: FrontendDonor[]; isZh: boolean; visible: boolean }) {
  const diamondDonors = donorsData.filter((d) => d.tier === 'diamond');

  return (
    <div className={`mb-8 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1B4F72]/10 border border-[#1B4F72]/20">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-vip-diamond-fill text-[#4682B4] text-sm" />
          </div>
          <span className="text-[#1B4F72] text-xs font-bold tracking-widest uppercase">
            {isZh ? '钻石捐赠人 · 至高荣誉' : 'Diamond Donors · Highest Honor'}
          </span>
        </div>
        <div className="h-px flex-1 bg-gradient-to-r from-[#4682B4]/25 to-transparent" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {diamondDonors.map((donor, idx) => (
          <DiamondBigCard key={donor.id} donor={donor} isZh={isZh} index={idx} />
        ))}
      </div>
    </div>
  );
}

/* ──────────────── Donors Honor Wall ──────────────── */
function DonorsHonorWall({ donorsData }: { donorsData: FrontendDonor[] }) {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [activeTier, setActiveTier] = useState<DonorTier | 'all'>('all');
  const [headerVis, setHeaderVis] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHeaderVis(true); }, { threshold: 0.1 });
    if (headerRef.current) obs.observe(headerRef.current);
    return () => obs.disconnect();
  }, []);

  // Logic: diamond donors shown in featured section; regular grid shows non-diamond (when 'all')
  const showDiamond = activeTier === 'all' || activeTier === 'diamond';
  const regularDonors = (() => {
    if (activeTier === 'all') return donorsData.filter((d) => d.tier !== 'diamond');
    if (activeTier === 'diamond') return [];
    return donorsData.filter((d) => d.tier === activeTier);
  })();

  const tierFilterLabels: Record<DonorTier | 'all', { zh: string; en: string }> = {
    all:       { zh: '全部',     en: 'All' },
    platinum:  { zh: '铂金',     en: 'Platinum' },
    diamond:   { zh: '钻石',     en: 'Diamond' },
    gold:      { zh: '金牌',     en: 'Gold' },
    silver:    { zh: '银牌',     en: 'Silver' },
    bronze:    { zh: '铜牌',     en: 'Bronze' },
    honorary:  { zh: '荣誉',     en: 'Honorary' },
    strategic: { zh: '战略伙伴', en: 'Strategic' },
  };

  return (
    <section className="py-20 bg-[#F7F9FC]" id="donors">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div
          ref={headerRef}
          className={`mb-12 transition-all duration-700 ${headerVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-4 tracking-widest uppercase">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-heart-line" />
                </div>
                {t('partnersPage.donors.badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-2">
                {t('partnersPage.donors.title')}
              </h2>
              <p className="text-[#0A2540]/50 text-base max-w-lg">
                {t('partnersPage.donors.subtitle')}
              </p>
            </div>
            {/* Stats */}
            <div className="flex items-center gap-6 shrink-0">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#0A2540]">{donorsData.length}</div>
                <div className="text-[#0A2540]/45 text-xs mt-0.5">{isZh ? '荣誉捐赠人' : 'Donors'}</div>
              </div>
              <div className="w-px h-10 bg-[#0A2540]/10" />
              <div className="text-center">
                <div className="text-2xl font-bold text-[#0A2540]">
                  {new Date().getFullYear() - Math.min(...donorsData.map((d) => d.year), 2022) + 1}
                </div>
                <div className="text-[#0A2540]/45 text-xs mt-0.5">{isZh ? '年持续支持' : 'Years of Support'}</div>
              </div>
              <div className="w-px h-10 bg-[#0A2540]/10" />
              <div className="text-center">
                <div className="text-2xl font-bold text-[#0A2540]">30+</div>
                <div className="text-[#0A2540]/45 text-xs mt-0.5">{isZh ? '覆盖国家' : 'Countries'}</div>
              </div>
            </div>
          </div>

          {/* Tier filter tabs */}
          <div className="flex items-center gap-2 flex-wrap mt-6">
            {TIER_FILTER_ORDER.map((tier) => {
              const meta = tier === 'all' ? null : TIER_META[tier];
              const label = tierFilterLabels[tier];
              const isActive = activeTier === tier;
              return (
                <button
                  key={tier}
                  onClick={() => setActiveTier(tier)}
                  className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    isActive
                      ? (meta ? meta.badgeCls : 'bg-[#0A2540] text-white')
                      : 'bg-white border border-[#0A2540]/10 text-[#0A2540]/60 hover:border-[#0A2540]/25 hover:text-[#0A2540]'
                  }`}
                >
                  {meta && <i className={`${meta.icon} text-[10px]`} />}
                  {isZh ? label.zh : label.en}
                </button>
              );
            })}
          </div>
        </div>

        {/* Diamond Featured Section */}
        {showDiamond && (
          <DiamondFeaturedSection donorsData={donorsData} isZh={isZh} visible={headerVis} />
        )}

        {/* Regular Donor Cards Grid */}
        {regularDonors.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4">
            {regularDonors.map((donor, i) => (
              <DonorCard key={donor.id} donor={donor} isZh={isZh} index={i} />
            ))}
          </div>
        )}

        {/* Empty state (only when a specific non-diamond tier has no entries) */}
        {!showDiamond && regularDonors.length === 0 && (
          <div className="text-center py-12 text-[#0A2540]/40 text-sm mb-4">
            {isZh ? '该类别暂无捐赠人' : 'No donors in this tier'}
          </div>
        )}

        {/* Become a donor CTA */}
        <div className={`mt-8 p-6 rounded-2xl bg-gradient-to-r from-[#0A2540] to-[#1B4F72] flex flex-col sm:flex-row items-center justify-between gap-4 transition-all duration-700 delay-300 ${headerVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div>
            <h3 className="text-white font-bold text-base mb-1">
              {isZh ? '成为 GTRA 的支持者' : 'Become a GTRA Supporter'}
            </h3>
            <p className="text-white/60 text-sm">
              {isZh ? '您的每一份捐赠，都将点亮一位科学家的未来' : "Your donation lights up a scientist's future"}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-2 text-white/50 text-xs">
              <i className="ri-shield-check-line text-[#E8A838]" />
              <span>{isZh ? '安全加密支付' : 'Secure Payment'}</span>
            </div>
            <div className="w-px h-4 bg-white/20" />
            <div className="flex items-center gap-2 text-white/50 text-xs">
              <i className="ri-global-line text-[#E8A838]" />
              <span>{isZh ? '支持多种货币' : 'Multi-currency'}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Partners Page ──────────────── */
export default function PartnersPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedType, setSelectedType] = useState<PartnerType | 'all'>('all');
  const [selectedRegion, setSelectedRegion] = useState<PartnerRegion | 'all'>('all');
  const [visible, setVisible] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [allDonors, setAllDonors] = useState<FrontendDonor[]>([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const donorsRef = useRef<HTMLDivElement>(null);
  const isZh = i18n.language === 'zh';

  // 从 API 加载数据
  useEffect(() => {
    async function loadData() {
      try {
        const donorsData = await getPublicDonors();
        setAllDonors(donorsData);
      } catch (error) {
        console.error('加载合作伙伴数据失败:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // 处理锚点跳转
  useEffect(() => {
    if (location.hash === '#donors' && donorsRef.current) {
      setTimeout(() => {
        donorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [location.hash]);

  // 可见性观察器
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // 从 allDonors 中分离合作伙伴和捐赠人
  // 合作伙伴: type = 'partner' (从后端)
  // 捐赠人: type = 'donor' (从后端)
  const partnersList = allDonors.filter((d) => d.type === 'partner') as Partner[];
  const donorsList = allDonors.filter((d) => d.type === 'donor');

  // 过滤合作伙伴
  const filteredPartners = partnersList.filter((p) => {
    if (selectedType !== 'all' && p.type !== selectedType) return false;
    if (selectedRegion !== 'all' && p.region !== selectedRegion) return false;
    return true;
  });

  const typeOptions = [
    { value: 'all' as const,        key: 'partnersPage.type.all' },
    { value: 'university' as const, key: 'partnersPage.type.university' },
    { value: 'research' as const,   key: 'partnersPage.type.research' },
    { value: 'enterprise' as const, key: 'partnersPage.type.enterprise' },
    { value: 'nonprofit' as const,  key: 'partnersPage.type.nonprofit' },
  ];

  const regionOptions = [
    { value: 'all' as const,      key: 'partnersPage.region.all' },
    { value: 'asia' as const,     key: 'partnersPage.region.asia' },
    { value: 'europe' as const,   key: 'partnersPage.region.europe' },
    { value: 'americas' as const, key: 'partnersPage.region.americas' },
    { value: 'africa' as const,   key: 'partnersPage.region.africa' },
    { value: 'oceania' as const,  key: 'partnersPage.region.oceania' },
  ];

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-64 md:h-80 flex items-center overflow-hidden pt-16">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 to-[#0A2540]/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/20 text-[#7EC8E3] text-xs font-semibold mb-4 tracking-widest uppercase border border-[#2C7DA0]/30">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-links-line" /></div>
            {t('partners.home.badge')}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">{t('partnersPage.hero.title')}</h1>
          <p className="text-white/70 text-base md:text-lg max-w-xl mb-6">{t('partnersPage.hero.subtitle')}</p>
          <a
            href="#apply"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-[#0A2540] font-semibold text-sm hover:bg-white/90 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line" /></div>
            {t('partnersPage.apply.title')}
          </a>
        </div>
      </section>

      {/* Filter & Content */}
      <section className="py-12 md:py-16" ref={ref}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Filter Bar */}
          <div className={`bg-white rounded-2xl p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[#0A2540]/40 text-xs font-medium whitespace-nowrap">{t('partnersPage.filter.type')}:</span>
              {typeOptions.map(({ value, key }) => (
                <button key={value} onClick={() => setSelectedType(value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${selectedType === value ? 'bg-[#0A2540] text-white' : 'bg-[#F7F9FC] text-[#0A2540]/60 hover:bg-[#0A2540]/10'}`}
                >
                  {t(key)}
                </button>
              ))}
            </div>
            <div className="hidden sm:block w-px h-6 bg-[#0A2540]/10 shrink-0" />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[#0A2540]/40 text-xs font-medium whitespace-nowrap">{t('partnersPage.filter.region')}:</span>
              {regionOptions.map(({ value, key }) => (
                <button key={value} onClick={() => setSelectedRegion(value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${selectedRegion === value ? 'bg-[#2C7DA0] text-white' : 'bg-[#F7F9FC] text-[#0A2540]/60 hover:bg-[#2C7DA0]/10'}`}
                >
                  {t(key)}
                </button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-1 bg-[#F7F9FC] p-1 rounded-lg shrink-0">
              <button onClick={() => setViewMode('grid')} className={`w-8 h-8 flex items-center justify-center rounded-md transition-all cursor-pointer ${viewMode === 'grid' ? 'bg-white text-[#0A2540]' : 'text-[#0A2540]/40 hover:text-[#0A2540]'}`}>
                <i className="ri-grid-line" />
              </button>
              <button onClick={() => setViewMode('list')} className={`w-8 h-8 flex items-center justify-center rounded-md transition-all cursor-pointer ${viewMode === 'list' ? 'bg-white text-[#0A2540]' : 'text-[#0A2540]/40 hover:text-[#0A2540]'}`}>
                <i className="ri-list-check" />
              </button>
            </div>
          </div>

          {/* Partners Grid/List */}
          <div className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} ${viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5' : 'flex flex-col gap-3'}`}>
            {filteredPartners.map((partner) => (
              <PartnerCard key={partner.id} partner={partner} isZh={isZh} view={viewMode} onSelect={setSelectedPartner} />
            ))}
            {filteredPartners.length === 0 && (
              <div className="col-span-full text-center py-16 text-[#0A2540]/40">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-search-line text-3xl" />
                </div>
                <p>{isZh ? '暂无符合条件的合作伙伴' : 'No partners found matching your criteria'}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Donors Honor Wall */}
      <div ref={donorsRef}>
        <DonorsHonorWall donorsData={donorsList} />
      </div>

      {/* Partnership Apply Form */}
      <PartnershipApplyForm />

      <Footer />

      {selectedPartner && (
        <PartnerDetailModal partner={selectedPartner} onClose={() => setSelectedPartner(null)} />
      )}
    </div>
  );
}
